import type { Question, QuestionOption, SurveyLanguage } from '../components/QuestionBuilder'

export type DisplaySize = 'small' | 'medium' | 'large' | 'original'

const mojibakeHints = /Ã|Â|â€|â€™|â€œ|â€|â€“|â€”|â€¢| /

const brokenEncodingHints = /[\u00c3\u00c2\ufffd]|\u00e2[\u0080-\u00bf]/

const mojibakeSignal = /[\u00c2\u00c3\u00e2\u0192\u201a\u201e\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u017d\u2018\u2019\u201c\u201d\u2022\u2013\u2014\u02dc\u2122\u0161\u203a\u0153\u017e\u0178\ufffd]/

const windows1252ExtraBytes: Record<number, number> = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
}

const encodeWindows1252Bytes = (value: string) => (
  Uint8Array.from(Array.from(value), (char) => {
    const code = char.charCodeAt(0)
    if (code <= 0xff) return code
    return windows1252ExtraBytes[code] ?? 0x3f
  })
)

const getMojibakeScore = (value: string) => (
  (mojibakeHints.test(value) ? 0 : 0) +
  (value.match(mojibakeSignal) || []).length +
  (value.match(/\ufffd/g) || []).length * 3
)

const decodeHtmlEntities = (value: string) => {
  if (!value || !/[&][a-zA-Z0-9#]+;/.test(value)) return value

  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = value
    return textarea.value
  }

  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
}

const repairUtf8Mojibake = (value: string) => {
  if (!mojibakeSignal.test(value) && !brokenEncodingHints.test(value)) return value

  try {
    const bytes = encodeWindows1252Bytes(value)
    const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes)
    const originalScore = getMojibakeScore(value)
    const decodedScore = getMojibakeScore(decoded)
    return decodedScore < originalScore ? decoded : value
  } catch {
    return value
  }
}

const repairReplacementCharacters = (value: string) => value
  .replace(/N O/g, 'NÃO')
  .replace(/N o/g, 'Não')
  .replace(/n o/g, 'não')
  .replace(/ UDIO/g, 'ÁUDIO')
  .replace(/ udio/g, 'Áudio')
  .replace(/ udio/g, 'áudio')
  .replace(/APLICA  O/g, 'APLICAÇÃO')
  .replace(/Aplica  o/g, 'Aplicação')
  .replace(/aplica  o/g, 'aplicação')
  .replace(/IN CIO/g, 'INÍCIO')
  .replace(/In cio/g, 'Início')
  .replace(/in cio/g, 'início')
  .replace(/HOR RIO/g, 'HORÁRIO')
  .replace(/Hor rio/g, 'Horário')
  .replace(/hor rio/g, 'horário')
  .replace(/N MERO/g, 'NÚMERO')
  .replace(/N mero/g, 'Número')
  .replace(/n mero/g, 'número')
  .replace(/P GINA/g, 'PÁGINA')
  .replace(/P gina/g, 'Página')
  .replace(/p gina/g, 'página')
  .replace(/M DIA/g, 'MÍDIA')
  .replace(/M dia/g, 'Mídia')
  .replace(/\bm dia\b/g, 'mídia')
  .replace(/V DEO/g, 'VÍDEO')
  .replace(/V deo/g, 'Vídeo')
  .replace(/v deo/g, 'vídeo')
  .replace(/OP  ES/g, 'OPÇÕES')
  .replace(/Op  es/g, 'Opções')
  .replace(/op  es/g, 'opções')
  .replace(/OP  O/g, 'OPÇÃO')
  .replace(/Op  o/g, 'Opção')
  .replace(/op  o/g, 'opção')
  .replace(/DESCRI  O/g, 'DESCRIÇÃO')
  .replace(/Descri  o/g, 'Descrição')
  .replace(/descri  o/g, 'descrição')
  .replace(/AVALIA  O/g, 'AVALIAÇÃO')
  .replace(/Avalia  o/g, 'Avaliação')
  .replace(/avalia  o/g, 'avaliação')
  .replace(/CLASSIFICA  O/g, 'CLASSIFICAÇÃO')
  .replace(/Classifica  o/g, 'Classificação')
  .replace(/classifica  o/g, 'classificação')
  .replace(/VALIDA  O/g, 'VALIDAÇÃO')
  .replace(/Valida  o/g, 'Validação')
  .replace(/valida  o/g, 'validação')
  .replace(/SELE  O/g, 'SELEÇÃO')
  .replace(/Sele  o/g, 'Seleção')
  .replace(/sele  o/g, 'seleção')
  .replace(/CONDI  ES/g, 'CONDIÇÕES')
  .replace(/Condi  es/g, 'Condições')
  .replace(/condi  es/g, 'condições')
  .replace(/CONDI  O/g, 'CONDIÇÃO')
  .replace(/Condi  o/g, 'Condição')
  .replace(/condi  o/g, 'condição')
  .replace(/CONFIGURA  O/g, 'CONFIGURAÇÃO')
  .replace(/Configura  o/g, 'Configuração')
  .replace(/configura  o/g, 'configuração')
  .replace(/PR XIMA/g, 'PRÓXIMA')
  .replace(/Pr xima/g, 'Próxima')
  .replace(/pr xima/g, 'próxima')
  .replace(/M LTIPLA/g, 'MÚLTIPLA')
  .replace(/M ltipla/g, 'Múltipla')
  .replace(/m ltipla/g, 'múltipla')
  .replace(/ NICA/g, 'ÚNICA')
  .replace(/ nica/g, 'Única')
  .replace(/ nica/g, 'única')
  .replace(/C PIA/g, 'CÓPIA')
  .replace(/C pia/g, 'Cópia')
  .replace(/c pia/g, 'cópia')
  .replace(/T TULO/g, 'TÍTULO')
  .replace(/T tulo/g, 'Título')
  .replace(/t tulo/g, 'título')
  .replace(/CONTE DO/g, 'CONTEÚDO')
  .replace(/Conte do/g, 'Conteúdo')
  .replace(/conte do/g, 'conteúdo')
  .replace(/VOC /g, 'VOCÊ')
  .replace(/Voc /g, 'Você')
  .replace(/voc /g, 'você')
  .replace(/EST /g, 'ESTÁ')
  .replace(/Est /g, 'Está')
  .replace(/est /g, 'está')
  .replace(/AT /g, 'ATÉ')
  .replace(/At /g, 'Até')
  .replace(/at /g, 'até')
  .replace(/([0-9A-Za-zÀ-ÿ]) +([0-9A-Za-zÀ-ÿ])/g, '$1 $2')
  .replace(/ +/g, ' ')
  .replace(/\s+([:;,.!?])/g, '$1')
  .replace(/\s*-\s*/g, ' - ')
  .replace(/[ \t]{2,}/g, ' ')

export const normalizeDisplayText = (value: string) => {
  let normalizedValue = value

  for (let index = 0; index < 3; index += 1) {
    const previousValue = normalizedValue
    normalizedValue = repairReplacementCharacters(repairUtf8Mojibake(decodeHtmlEntities(normalizedValue)))
    if (normalizedValue === previousValue) break
  }

  return normalizedValue
    .replace(/\(\s*C[^)]*pia\s*\)/gi, '(Copia)')
    .replace(/\(\s*CÃƒÆ’Ã†â€™[^)]*pia\s*\)/gi, '(Copia)')
    .replace(/\(\s*Cópia\s*\)/gi, '(Copia)')
}

const hasHtmlMarkup = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value)

const normalizeHtmlString = (value: string) => (
  value
    .split(/(<[^>]+>)/g)
    .map((part) => (part.startsWith('<') && part.endsWith('>') ? part : normalizeDisplayText(part)))
    .join('')
)

const normalizeDisplaySize = (size?: DisplaySize | 'normal' | string): DisplaySize => {
  switch (size) {
    case 'small':
    case 'medium':
    case 'large':
    case 'original':
      return size
    case 'normal':
      return 'original'
    default:
      return 'original'
  }
}

const extractDeltaText = (value: unknown) => {
  if (!Array.isArray(value)) return ''

  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return ''
      const insertValue = (item as { insert?: unknown }).insert
      if (typeof insertValue === 'string') return insertValue
      if (typeof insertValue === 'number' || typeof insertValue === 'boolean') return String(insertValue)
      return ''
    })
    .join('')
}

export const ensureHtmlString = (value?: unknown): string => {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') {
    return hasHtmlMarkup(value) ? normalizeHtmlString(value) : normalizeDisplayText(value)
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value)
  }

  if (Array.isArray(value)) {
    return value.map((item) => ensureHtmlString(item)).filter(Boolean).join(' ')
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>

    if (typeof record.html === 'string') return normalizeHtmlString(record.html)
    if (typeof record.text === 'string') return normalizeDisplayText(record.text)
    if (typeof record.value === 'string') return normalizeDisplayText(record.value)
    if (typeof record.value === 'number' || typeof record.value === 'boolean') return String(record.value)

    const deltaText = extractDeltaText(record.ops)
    if (deltaText) return deltaText

    if (record.title !== undefined) {
      const titleValue = ensureHtmlString(record.title)
      if (titleValue) return titleValue
    }

    if (record.description !== undefined) {
      const descriptionValue = ensureHtmlString(record.description)
      if (descriptionValue) return descriptionValue
    }

    try {
      return JSON.stringify(record)
    } catch {
      return String(record)
    }
  }

  return String(value)
}

export const SURVEY_LANGUAGE_LABELS: Record<SurveyLanguage, string> = {
  pt: 'Idioma original',
  en: 'English',
  es: 'Espanol',
}

const AUTO_TRANSLATION_DICTIONARY: Record<Exclude<SurveyLanguage, 'pt'>, Record<string, string>> = {
  en: {
    'nao se aplica': 'Not applicable',
    'nao sei': "I don't know",
    'nenhuma das anteriores': 'None of the above',
    'todas as anteriores': 'All of the above',
    'pesquisa concluida': 'Survey completed',
    'outro': 'Other',
    'outros': 'Others',
    'outros motivos': 'Other reasons',
    'digite sua resposta': 'Type your answer',
    'selecione uma opcao': 'Select an option',
    'selecione as opcoes': 'Select the options',
    'selecione todas as opcoes que se aplicam': 'Select all options that apply',
    'selecione todas que se aplicam': 'Select all that apply',
    'qual e a sua': 'What is your',
    'qual e o seu': 'What is your',
    'qual sua': 'What is your',
    'qual seu': 'What is your',
    'quais sao': 'What are',
    'quais os': 'Which are the',
    'quais as': 'Which are the',
    'em uma escala de': 'On a scale of',
    'em relacao a': 'In relation to',
    'o quanto voce': 'How much do you',
    'com que frequencia': 'How often',
    'ordene os itens': 'Rank the items',
    'classifique cada item': 'Rate each item',
    'de acordo com sua preferencia': 'according to your preference',
    'pequeno': 'Small',
    'medio': 'Medium',
    'grande': 'Large',
    'original': 'Original',
    'voce chegou ao encerramento desta pesquisa': 'You have reached the end of this survey',
    'nenhuma pergunta disponivel': 'No question available',
    'tamanho configurado': 'Configured size',
    'obrigatoria': 'required',
    'enviando': 'Submitting',
    'muito satisfeito': 'Very satisfied',
    'satisfeito': 'Satisfied',
    'neutro': 'Neutral',
    'insatisfeito': 'Dissatisfied',
    'muito insatisfeito': 'Very dissatisfied',
    'muito provavel': 'Very likely',
    'provavel': 'Likely',
    'pouco provavel': 'Unlikely',
    'nada provavel': 'Not at all likely',
    'extremamente provavel': 'Extremely likely',
    'concordo totalmente': 'Strongly agree',
    'concordo parcialmente': 'Somewhat agree',
    'discordo totalmente': 'Strongly disagree',
    'discordo parcialmente': 'Somewhat disagree',
    'nem concordo nem discordo': 'Neither agree nor disagree',
    'muito bom': 'Very good',
    'bom': 'Good',
    'regular': 'Average',
    'ruim': 'Poor',
    'muito ruim': 'Very poor',
    'otimo': 'Great',
    'pessimo': 'Terrible',
    'excelente': 'Excellent',
    'sim, com certeza': 'Yes, definitely',
    'nao, de forma alguma': 'No, not at all',
    'sim': 'Yes',
    'nao': 'No',
    'talvez': 'Maybe',
    'qual': 'what',
    'quais': 'which',
    'quanto': 'how much',
    'quantos': 'how many',
    'quantas': 'how many',
    'como': 'how',
    'quando': 'when',
    'onde': 'where',
    'por que': 'why',
    'porque': 'because',
    'voce': 'you',
    'voces': 'you',
    'seu': 'your',
    'sua': 'your',
    'seus': 'your',
    'suas': 'your',
    'entre': 'among',
    'esses': 'these',
    'essas': 'these',
    'este': 'this',
    'esta': 'this',
    'estes': 'these',
    'estas': 'these',
    'gosta': 'like',
    'gostou': 'liked',
    'mais': 'more',
    'menos': 'less',
    'melhor': 'best',
    'pior': 'worst',
    'prefere': 'prefer',
    'preferencia': 'preference',
    'preferido': 'preferred',
    'preferida': 'preferred',
    'favorito': 'favorite',
    'favorita': 'favorite',
    'principal': 'main',
    'principais': 'main',
    'marca': 'brand',
    'marcas': 'brands',
    'produto': 'product',
    'produtos': 'products',
    'servico': 'service',
    'servicos': 'services',
    'empresa': 'company',
    'empresas': 'companies',
    'mercado': 'market',
    'mercados': 'markets',
    'loja': 'store',
    'lojas': 'stores',
    'compra': 'purchase',
    'compras': 'purchases',
    'comprou': 'bought',
    'consome': 'consume',
    'consumo': 'consumption',
    'frequencia': 'frequency',
    'avaliacao': 'evaluation',
    'avalie': 'rate',
    'nota': 'score',
    'satisfacao': 'satisfaction',
    'motivo': 'reason',
    'motivos': 'reasons',
    'comentario': 'comment',
    'comentarios': 'comments',
    'resposta': 'answer',
    'respostas': 'answers',
    'pergunta': 'question',
    'perguntas': 'questions',
    'pesquisa': 'survey',
    'cliente': 'customer',
    'clientes': 'customers',
    'idade': 'age',
    'genero': 'gender',
    'cidade': 'city',
    'estado': 'state',
    'bairro': 'neighborhood',
    'renda': 'income',
    'escolaridade': 'education',
    'trabalho': 'work',
    'preco': 'price',
    'precos': 'prices',
    'qualidade': 'quality',
    'atendimento': 'customer service',
    'experiencia': 'experience',
    'recomendaria': 'would recommend',
    'recomenda': 'recommend',
    'concordo': 'agree',
    'discordo': 'disagree',
    'totalmente': 'strongly',
    'parcialmente': 'partly',
    'sempre': 'always',
    'frequentemente': 'often',
    'as vezes': 'sometimes',
    'raramente': 'rarely',
    'nunca': 'never',
    'diariamente': 'daily',
    'semanalmente': 'weekly',
    'mensalmente': 'monthly',
    'anualmente': 'yearly',
    'opcao': 'option',
    'opcoes': 'options',
    'anterior': 'previous',
    'anteriores': 'previous',
    'aplica': 'applies',
    'sei': 'know',
    'alta': 'High',
    'alto': 'High',
    'baixa': 'Low',
    'baixo': 'Low',
    'muito alta': 'Very high',
    'muito alto': 'Very high',
    'muito baixa': 'Very low',
    'muito baixo': 'Very low',
  },
  es: {
    'nao se aplica': 'No aplica',
    'nao sei': 'No lo se',
    'nenhuma das anteriores': 'Ninguna de las anteriores',
    'todas as anteriores': 'Todas las anteriores',
    'pesquisa concluida': 'Encuesta completada',
    'outro': 'Otro',
    'outros': 'Otros',
    'outros motivos': 'Otros motivos',
    'digite sua resposta': 'Escriba su respuesta',
    'selecione uma opcao': 'Seleccione una opcion',
    'selecione as opcoes': 'Seleccione las opciones',
    'selecione todas as opcoes que se aplicam': 'Seleccione todas las opciones que correspondan',
    'selecione todas que se aplicam': 'Seleccione todas las que correspondan',
    'qual e a sua': 'Cual es su',
    'qual e o seu': 'Cual es su',
    'qual sua': 'Cual es su',
    'qual seu': 'Cual es su',
    'quais sao': 'Cuales son',
    'quais os': 'Cuales son los',
    'quais as': 'Cuales son las',
    'em uma escala de': 'En una escala de',
    'em relacao a': 'En relacion con',
    'o quanto voce': 'Que tanto usted',
    'com que frequencia': 'Con que frecuencia',
    'ordene os itens': 'Ordene los elementos',
    'classifique cada item': 'Clasifique cada elemento',
    'de acordo com sua preferencia': 'segun su preferencia',
    'pequeno': 'Pequeno',
    'medio': 'Mediano',
    'grande': 'Grande',
    'original': 'Original',
    'voce chegou ao encerramento desta pesquisa': 'Ha llegado al final de esta encuesta',
    'nenhuma pergunta disponivel': 'No hay preguntas disponibles',
    'tamanho configurado': 'Tamano configurado',
    'obrigatoria': 'obligatoria',
    'enviando': 'Enviando',
    'muito satisfeito': 'Muy satisfecho',
    'satisfeito': 'Satisfecho',
    'neutro': 'Neutral',
    'insatisfeito': 'Insatisfecho',
    'muito insatisfeito': 'Muy insatisfecho',
    'muito provavel': 'Muy probable',
    'provavel': 'Probable',
    'pouco provavel': 'Poco probable',
    'nada provavel': 'Nada probable',
    'extremamente provavel': 'Extremadamente probable',
    'concordo totalmente': 'Totalmente de acuerdo',
    'concordo parcialmente': 'Parcialmente de acuerdo',
    'discordo totalmente': 'Totalmente en desacuerdo',
    'discordo parcialmente': 'Parcialmente en desacuerdo',
    'nem concordo nem discordo': 'Ni de acuerdo ni en desacuerdo',
    'muito bom': 'Muy bueno',
    'bom': 'Bueno',
    'regular': 'Regular',
    'ruim': 'Malo',
    'muito ruim': 'Muy malo',
    'otimo': 'Optimo',
    'pessimo': 'Pesimo',
    'excelente': 'Excelente',
    'sim, com certeza': 'Si, definitivamente',
    'nao, de forma alguma': 'No, de ninguna manera',
    'sim': 'Si',
    'nao': 'No',
    'talvez': 'Tal vez',
    'qual': 'cual',
    'quais': 'cuales',
    'quanto': 'cuanto',
    'quantos': 'cuantos',
    'quantas': 'cuantas',
    'como': 'como',
    'quando': 'cuando',
    'onde': 'donde',
    'por que': 'por que',
    'porque': 'porque',
    'voce': 'usted',
    'voces': 'ustedes',
    'seu': 'su',
    'sua': 'su',
    'seus': 'sus',
    'suas': 'sus',
    'entre': 'entre',
    'esses': 'estos',
    'essas': 'estas',
    'este': 'este',
    'esta': 'esta',
    'estes': 'estos',
    'estas': 'estas',
    'gosta': 'le gusta',
    'gostou': 'le gusto',
    'mais': 'mas',
    'menos': 'menos',
    'melhor': 'mejor',
    'pior': 'peor',
    'prefere': 'prefiere',
    'preferencia': 'preferencia',
    'preferido': 'preferido',
    'preferida': 'preferida',
    'favorito': 'favorito',
    'favorita': 'favorita',
    'principal': 'principal',
    'principais': 'principales',
    'marca': 'marca',
    'marcas': 'marcas',
    'produto': 'producto',
    'produtos': 'productos',
    'servico': 'servicio',
    'servicos': 'servicios',
    'empresa': 'empresa',
    'empresas': 'empresas',
    'mercado': 'mercado',
    'mercados': 'mercados',
    'loja': 'tienda',
    'lojas': 'tiendas',
    'compra': 'compra',
    'compras': 'compras',
    'comprou': 'compro',
    'consome': 'consume',
    'consumo': 'consumo',
    'frequencia': 'frecuencia',
    'avaliacao': 'evaluacion',
    'avalie': 'evalue',
    'nota': 'calificacion',
    'satisfacao': 'satisfaccion',
    'motivo': 'motivo',
    'motivos': 'motivos',
    'comentario': 'comentario',
    'comentarios': 'comentarios',
    'resposta': 'respuesta',
    'respostas': 'respuestas',
    'pergunta': 'pregunta',
    'perguntas': 'preguntas',
    'pesquisa': 'encuesta',
    'cliente': 'cliente',
    'clientes': 'clientes',
    'idade': 'edad',
    'genero': 'genero',
    'cidade': 'ciudad',
    'estado': 'estado',
    'bairro': 'barrio',
    'renda': 'ingresos',
    'escolaridade': 'educacion',
    'trabalho': 'trabajo',
    'preco': 'precio',
    'precos': 'precios',
    'qualidade': 'calidad',
    'atendimento': 'atencion al cliente',
    'experiencia': 'experiencia',
    'recomendaria': 'recomendaria',
    'recomenda': 'recomienda',
    'concordo': 'de acuerdo',
    'discordo': 'en desacuerdo',
    'totalmente': 'totalmente',
    'parcialmente': 'parcialmente',
    'sempre': 'siempre',
    'frequentemente': 'frecuentemente',
    'as vezes': 'a veces',
    'raramente': 'raramente',
    'nunca': 'nunca',
    'diariamente': 'diariamente',
    'semanalmente': 'semanalmente',
    'mensalmente': 'mensualmente',
    'anualmente': 'anualmente',
    'opcao': 'opcion',
    'opcoes': 'opciones',
    'anterior': 'anterior',
    'anteriores': 'anteriores',
    'aplica': 'aplica',
    'sei': 'se',
    'alta': 'Alta',
    'alto': 'Alto',
    'baixa': 'Baja',
    'baixo': 'Bajo',
    'muito alta': 'Muy alta',
    'muito alto': 'Muy alto',
    'muito baixa': 'Muy baja',
    'muito baixo': 'Muy bajo',
  },
}

const normalizeForTranslation = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase()

const hasRealTranslation = (translated: string | undefined, original: string) => (
  !!translated && normalizeForTranslation(stripHtml(translated)) !== normalizeForTranslation(stripHtml(original))
)

const matchCapitalization = (source: string, translated: string) => {
  if (!source) return translated
  if (source.toUpperCase() === source) return translated.toUpperCase()
  if (source[0] === source[0].toUpperCase()) {
    return translated.charAt(0).toUpperCase() + translated.slice(1)
  }
  return translated
}

export const autoTranslateText = (value: string, language: SurveyLanguage): string => {
  if (language === 'pt' || !value) return value

  const dictionary = AUTO_TRANSLATION_DICTIONARY[language]
  let translated = value
  const phrases = Object.keys(dictionary).sort((left, right) => right.length - left.length)

  phrases.forEach((phrase) => {
    if (!phrase.includes(' ')) return
    const escapedPhrase = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+')
    translated = translated.replace(new RegExp(`\\b${escapedPhrase}\\b`, 'gi'), (match) => (
      matchCapitalization(match, dictionary[phrase])
    ))
  })

  return translated.replace(/\b[\p{L}]+\b/gu, (match) => {
    const translatedWord = dictionary[normalizeForTranslation(match)]
    return translatedWord ? matchCapitalization(match, translatedWord) : match
  })
}

export const autoTranslateHtml = (value: string, language: SurveyLanguage): string => {
  const normalizedValue = ensureHtmlString(value)
  if (language === 'pt' || !normalizedValue) return normalizedValue

  return normalizedValue
    .split(/(<[^>]+>)/g)
    .map((part) => (part.startsWith('<') && part.endsWith('>') ? part : autoTranslateText(part, language)))
    .join('')
}

export const stripHtml = (value?: unknown) => {
  const normalizedValue = ensureHtmlString(value)
  if (!normalizedValue) return ''

  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div')
    tmp.innerHTML = normalizedValue
    return (tmp.innerText || tmp.textContent || '').replace(/\s+/g, ' ').trim()
  }

  return normalizedValue
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const getLocalizedQuestionContent = (
  question: Question,
  language: SurveyLanguage,
  fallbackTitle?: string,
  fallbackDescription?: string
) => {
  const localized = question.translations?.[language]
  const originalTitle = ensureHtmlString(fallbackTitle || question.title)
  const originalDescription = ensureHtmlString(fallbackDescription || question.description || '')
  const savedTitle = ensureHtmlString(localized?.title || '')
  const savedDescription = ensureHtmlString(localized?.description || '')
  const savedTitleIsTranslated = (
    hasRealTranslation(savedTitle, ensureHtmlString(question.title)) &&
    hasRealTranslation(savedTitle, originalTitle)
  )
  const savedDescriptionIsTranslated = (
    hasRealTranslation(savedDescription, ensureHtmlString(question.description || '')) &&
    hasRealTranslation(savedDescription, originalDescription)
  )

  return {
    title: language === 'pt'
      ? originalTitle
      : savedTitleIsTranslated
        ? savedTitle
        : autoTranslateHtml(originalTitle, language),
    description: language === 'pt'
      ? originalDescription
      : savedDescriptionIsTranslated
        ? savedDescription
        : autoTranslateHtml(originalDescription, language),
  }
}

export const getLocalizedOptionText = (
  option: QuestionOption,
  language: SurveyLanguage
) => {
  const originalText = ensureHtmlString(option.text)
  const savedText = ensureHtmlString(option.translations?.[language] || '')

  if (language === 'pt') return originalText

  return hasRealTranslation(savedText, originalText)
    ? savedText
    : autoTranslateHtml(originalText, language)
}

export const localizeQuestionOptions = (
  options: QuestionOption[] | undefined,
  language: SurveyLanguage
) => (options || []).map((option) => ({
  ...option,
  text: getLocalizedOptionText(option, language),
}))

const getDisplaySizeHint = (size?: DisplaySize) => {
  switch (normalizeDisplaySize(size)) {
    case 'small':
      return 'Pequeno'
    case 'medium':
      return 'Médio'
    case 'large':
      return 'Grande'
    case 'original':
    default:
      return 'Original'
  }
}

export const getImageDisplayWidthClass = (size?: Question['imageDisplaySize']) => {
  switch (normalizeDisplaySize(size)) {
    case 'small':
      return 'max-w-xs'
    case 'medium':
      return 'max-w-md'
    case 'large':
      return 'max-w-2xl'
    case 'original':
    default:
      return 'max-w-full'
  }
}

export const getImageDisplayHint = (size?: Question['imageDisplaySize']) =>
  getDisplaySizeHint(normalizeDisplaySize(size))

export const getLogoDisplayWidthClass = (size?: DisplaySize) => {
  switch (normalizeDisplaySize(size)) {
    case 'small':
      return 'w-full max-w-[7rem] sm:max-w-[8rem]'
    case 'medium':
      return 'w-full max-w-[10rem] sm:max-w-[12rem]'
    case 'large':
      return 'w-full max-w-[14rem] sm:max-w-[18rem]'
    case 'original':
    default:
      return 'w-full max-w-full'
  }
}

export const getLogoDisplayImageClass = (size?: DisplaySize) => {
  switch (normalizeDisplaySize(size)) {
    case 'small':
      return 'max-h-16'
    case 'medium':
      return 'max-h-24'
    case 'large':
      return 'max-h-32'
    case 'original':
    default:
      return 'max-h-[40vh]'
  }
}

export const getLogoDisplayHint = (size?: DisplaySize) =>
  getDisplaySizeHint(normalizeDisplaySize(size))

export const getLogoDisplayPrintWidth = (size?: DisplaySize) => {
  switch (normalizeDisplaySize(size)) {
    case 'small':
      return '128px'
    case 'medium':
      return '192px'
    case 'large':
      return '288px'
    case 'original':
    default:
      return '100%'
  }
}

export const getLogoDisplayPrintHeight = (size?: DisplaySize) => {
  switch (normalizeDisplaySize(size)) {
    case 'small':
      return '64px'
    case 'medium':
      return '96px'
    case 'large':
      return '128px'
    case 'original':
    default:
      return '220px'
  }
}
